import React from "react";
import { InputSwitch } from "primereact/inputswitch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { PortalDsBadge } from "src/components/portal-design-system/portal-ds-badge";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import { cn } from "src/lib/utils";
import { formatCurrency, formatDate } from "../../utils/billing-utils";
import type { CommissionInvoice } from "../types/commissions.types";
import { CommissionTableEmptyState } from "./commission-table-empty-state";

function monthYearLabel(month: number, year: number): string {
  const date = new Date(year, month - 1, 1);
  return date.toLocaleString("default", { month: "long", year: "numeric" });
}

interface CommissionInvoicesTableProps {
  invoices: CommissionInvoice[];
  onRowClick: (invoice: CommissionInvoice) => void;
  onToggleIncludeInTotal?: (invoice: CommissionInvoice, includeInTotal: boolean) => void;
}

const CommissionInvoicesTable: React.FC<CommissionInvoicesTableProps> = ({
  invoices,
  onRowClick,
  onToggleIncludeInTotal,
}) => {
  return (
    <div className={portalDs.tableContainer}>
      <Table>
        <TableHeader>
          <TableRow className={portalDs.tableHead}>
            <TableHead className={portalDs.tableHeadCell}>Invoice #</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Company</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Amount</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Month</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Status</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Invoice Date</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Paid Date</TableHead>
            <TableHead className={cn(portalDs.tableHeadCell, "w-[100px]")}>In total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.length === 0 ? (
            <CommissionTableEmptyState
              colSpan={8}
              title="No paid invoices for this period"
              description="Try a different month or quarter, or load invoices from QuickBooks."
            />
          ) : (
            invoices.map((inv) => (
              <TableRow
                key={inv.id}
                className={cn(portalDs.tableBodyRow, portalDs.tableBodyRowInteractive)}
                onClick={() => onRowClick(inv)}
              >
                <TableCell className={cn(portalDs.tableCell, "font-medium")}>
                  {inv.invoiceNumber}
                </TableCell>
                <TableCell className={portalDs.tableCell}>{inv.companyName}</TableCell>
                <TableCell className={portalDs.tableCell}>{formatCurrency(inv.amount)}</TableCell>
                <TableCell className={portalDs.tableCell}>
                  {inv.month != null && inv.year != null
                    ? monthYearLabel(inv.month, inv.year)
                    : "—"}
                </TableCell>
                <TableCell className={portalDs.tableCell}>
                  <PortalDsBadge
                    pill
                    variant={inv.status === "paid" ? "green" : "amber"}
                  >
                    {inv.status}
                  </PortalDsBadge>
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  {inv.createdAt ? formatDate(inv.createdAt) : "—"}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  {inv.paidDate ? formatDate(inv.paidDate) : "—"}
                </TableCell>
                <TableCell
                  className={portalDs.tableCell}
                  onClick={(e) => e.stopPropagation()}
                >
                  <InputSwitch
                    checked={inv.includeInTotal !== false}
                    onChange={(e) =>
                      onToggleIncludeInTotal?.(inv, e.value ?? true)
                    }
                    disabled={!onToggleIncludeInTotal}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default CommissionInvoicesTable;
